import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { friendlyError } from "@/lib/api";

// Network + offline order queue. Orders taken with no signal are kept locally
// and sent when the connection returns. Nothing is lost.
const NetworkContext = createContext(null);
const QKEY = "reto_order_queue";

function readQueue() {
  try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; }
}
function writeQueue(q) { localStorage.setItem(QKEY, JSON.stringify(q)); }

export function NetworkProvider({ children }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(readQueue);
  const flushing = useRef(false);

  const refresh = useCallback(() => setQueue(readQueue()), []);

  // Add an order to the local queue (used when offline OR as a safe outbox).
  const enqueueOrder = useCallback((payload) => {
    const q = readQueue();
    const item = { ...payload, clientRef: payload.clientRef || `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
    q.push(item);
    writeQueue(q);
    setQueue(q);
    return item;
  }, []);

  const flush = useCallback(async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    try {
      let q = readQueue();
      while (q.length) {
        const item = q[0];
        try {
          await api.post("/orders", item); // idempotent by clientRef on the server
        } catch (e) {
          // Stop on the first real network failure; keep the rest queued.
          if (!navigator.onLine) break;
          // A bad item (e.g. sold out) should not block the queue forever.
          break;
        }
        q = readQueue().slice(1);
        writeQueue(q);
        setQueue(q);
      }
    } finally {
      flushing.current = false;
    }
  }, []);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flush(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    flush();
    const t = setInterval(flush, 15000);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); clearInterval(t); };
  }, [flush]);

  const value = { online, queueCount: queue.length, enqueueOrder, flush, refresh };
  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>;
}

export function useNetwork() {
  const ctx = useContext(NetworkContext);
  if (!ctx) throw new Error("useNetwork must be used inside NetworkProvider");
  return ctx;
}
